// Provider-supplied labels (caller names, group names, source names) arrive
// with emoji in the raw JSON. CONTRACT "Data the builders must use": those
// labels render with the emoji stripped, and DESIGN-007 bans emoji anywhere
// in the product, so the strip happens at the render boundary rather than in
// each view. Pure and allocation-light: the common case has no match.

// Pictographs, the variation selectors and zero-width joiner that build
// sequences, skin-tone modifiers, regional indicator pairs, and the keycap
// combiner. Digits and "#" keep their meaning unless a keycap follows.
const EMOJI = /(?:\p{Regional_Indicator}\p{Regional_Indicator})|(?:[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}](?:️|‍|[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}])*)|(?:[0-9#*]️?⃣)/gu;
const COLLAPSE = /\s{2,}/gu;

export function hasEmoji(value: string): boolean {
  EMOJI.lastIndex = 0;
  return EMOJI.test(value);
}

// Returns the label without emoji, with the whitespace the removal left
// behind collapsed. A label that was only emoji returns "" so callers can
// fall back to an identifier instead of printing a blank name.
export function stripEmoji(value: string): string {
  if (!value) return '';
  return value.replace(EMOJI, ' ').replace(COLLAPSE, ' ').trim();
}

// Display helper: the stripped label, or the fallback when nothing survives.
export function displayLabel(value: string | null | undefined, fallback: string): string {
  const stripped = stripEmoji(value ?? '');
  return stripped === '' ? fallback : stripped;
}

// Some senders write words with decorative letterforms instead of letters:
// mathematical alphanumerics (U+1D400 to U+1D7FF), enclosed alphanumerics
// (U+2460 to U+24FF) and the enclosed alphanumeric supplement below the
// regional indicators (U+1F100 to U+1F1E5). They are not pictographs, so the
// strip above leaves them, and no product font covers them, so they render as
// a fallback glyph or a colour badge. They fold back to the letter or digit
// they stand for, which keeps the word readable; deleting them would take a
// letter out of a name.
const SUBSTITUTE = /[\u{1D400}-\u{1D7FF}\u{2460}-\u{24FF}\u{1F100}-\u{1F1E5}]/gu;
// Negative circled (U+1F150) and negative squared (U+1F170) capitals have no
// compatibility decomposition, so they are folded by position: 26 letters each.
const NEGATIVE_BLOCKS = [0x1f150, 0x1f170] as const;
const LETTERS_IN_BLOCK = 26;

function foldOne(match: string): string {
  const code = match.codePointAt(0) ?? 0;
  for (const start of NEGATIVE_BLOCKS) {
    if (code >= start && code < start + LETTERS_IN_BLOCK) return String.fromCharCode(0x41 + (code - start));
  }
  const folded = match.normalize('NFKC');
  return folded === match ? ' ' : folded;
}

export function foldSubstituteLetters(value: string): string {
  return value.replace(SUBSTITUTE, foldOne);
}

// Provider prose ready to render inside the product: substitute letterforms
// folded back to letters, then every pictograph removed (DESIGN-007, CONTRACT
// polish item 19). Use this wherever a raw message, post or bio is printed.
export function plainProviderText(value: string): string {
  return stripEmoji(foldSubstituteLetters(value));
}
